import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/auth';
import { useGameStore } from '@/stores/game';
import { extractErrorMessage } from '@/utils/errorHandler';
import { getClarityHeaders } from '@/utils/clarity';

const apiBaseUrl = process.env.VUE_APP_API_BASE_URL as string;
const api: AxiosInstance = axios.create({
  baseURL: apiBaseUrl
});

/**
 * Dynamically update the API base URL.
 * Essential for iframe integrations where the parent provides the endpoint.
 */
export function setApiBaseUrl(url: string): void {
  if (!url) return;
  
  let formattedUrl = url.trim();
  if (formattedUrl.endsWith('/')) {
    formattedUrl = formattedUrl.slice(0, -1);
  }

  // Ensure /api/v1 is present if it's a domain-only URL
  // but avoid double-prefixing if it's already there
  if (!formattedUrl.includes('/api/v1')) {
    formattedUrl = `${formattedUrl}/api/v1`;
  }
  
  api.defaults.baseURL = formattedUrl;
  console.log(`[API] Base URL updated to: ${formattedUrl}. Interceptor will now prefix requests if necessary.`);
}

const AUTH_REQUIRED_ENDPOINTS = [
  '/wagers',
  '/wager-details',
  '/place-bet',
  '/book-bet',
  '/load-booked-bet',
  '/booked-bets',
];

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  config.params = { ...(config.params || {}) };

  // --- Nginx Unified Domain Handling ---
  // If config.url starts with / but is not /api/v1, and baseURL has /api/v1, 
  // Axios will normally discard the /api/v1. We must force it back.
  if (config.url && config.url.startsWith('/') && !config.url.startsWith('/api/v1')) {
    const currentBaseUrl = config.baseURL || api.defaults.baseURL || '';
    if (typeof currentBaseUrl === 'string' && currentBaseUrl.includes('/api/v1')) {
      try {
        // Construct absolute URL for checking
        let urlObj: URL;
        if (currentBaseUrl.startsWith('http')) {
          urlObj = new URL(currentBaseUrl);
        } else {
          // Relative baseURL handling
          const base = window.location.origin;
          const fullPath = currentBaseUrl.startsWith('/') ? currentBaseUrl : `/${currentBaseUrl}`;
          urlObj = new URL(fullPath, base);
        }

        if (urlObj.pathname.includes('/api/v1')) {
          const prefix = urlObj.pathname.endsWith('/') ? urlObj.pathname.slice(0, -1) : urlObj.pathname;
          // Prepend prefix to the url
          const urlWithoutSlash = config.url.startsWith('/') ? config.url.substring(1) : config.url;
          config.url = `${prefix}/${urlWithoutSlash}`;
          // Set baseURL to just the origin so axios doesn't strip the prefix in config.url
          config.baseURL = urlObj.origin;
        }
      } catch (e) {
        // Fallback for malformed URLs
        if (currentBaseUrl.endsWith('/api/v1')) {
          config.url = `/api/v1${config.url}`;
          config.baseURL = currentBaseUrl.replace(/\/api\/v1$/, '');
        }
      }
    }
  }

  // --- Standard Interceptors ---
  const clarityHeaders = getClarityHeaders();
  Object.assign(config.headers, clarityHeaders);

  const authStore = useAuthStore();
  authStore.reinitializeFromSession();
  
  if (config.url?.includes('/game-settings')) {
    const gameStore = useGameStore();
    const account = authStore.account || sessionStorage.getItem('auth_account');
    if (account) config.headers['auth-account'] = account;
    
    const currency = gameStore.getBetSettings?.currency_code || sessionStorage.getItem('auth_currency');
    if (currency) config.headers['auth-currency'] = currency;
  }

  const requiresAuth = AUTH_REQUIRED_ENDPOINTS.some(endpoint => config.url?.includes(endpoint));

  if (requiresAuth) {
    if (!authStore.isAuthenticated) {
      return authStore.authenticate()
        .then(() => {
          if (authStore.isAuthenticated) Object.assign(config.headers, authStore.getAuthHeaders);
          return config;
        })
        .catch((error) => {
          console.warn('[API] Auth failed, proceeding without headers:', error);
          return config;
        });
    }
    Object.assign(config.headers, authStore.getAuthHeaders);
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      useAuthStore().clearInvalidCredentials();
    }

    const errorMessage = error?.response?.data?.message || error?.message || '';
    if (errorMessage.toLowerCase().includes('token expired or invalid')) {
      useAuthStore().clearInvalidCredentials();
    }

    error.userMessage = extractErrorMessage(error);
    return Promise.reject(error);
  }
);

export default api;
